import html2canvas from 'html2canvas-pro';
import type { Post } from '../types';

export interface ExportPostPreviewData {
  blob: Blob;
  dataUrl: string;
  filename: string;
}

/**
 * Export a single post preview as PNG image
 * Captures the preview element and adds Kontentino watermark
 */
export async function exportPostPreviewAsPNG(
  previewElement: HTMLElement,
  post: Post
): Promise<ExportPostPreviewData> {
  // Clone the element to avoid modifying the original
  const clonedElement = previewElement.cloneNode(true) as HTMLElement;

  // Temporarily add to document for proper rendering
  clonedElement.style.position = 'absolute';
  clonedElement.style.left = '-9999px';
  clonedElement.style.top = '-9999px';
  document.body.appendChild(clonedElement);

  try {
    const canvas = await html2canvas(clonedElement, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
    });

    // Remove from document
    document.body.removeChild(clonedElement);

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    // Add watermark at bottom
    const watermarkHeight = 40;
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = canvas.width;
    finalCanvas.height = canvas.height + watermarkHeight;
    const finalCtx = finalCanvas.getContext('2d');

    if (!finalCtx) throw new Error('Failed to get final canvas context');

    // White background
    finalCtx.fillStyle = '#ffffff';
    finalCtx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

    // Draw preview
    finalCtx.drawImage(canvas, 0, 0);

    // Draw watermark text
    finalCtx.fillStyle = '#666666';
    finalCtx.font = '16px system-ui, -apple-system, sans-serif';
    finalCtx.textAlign = 'center';
    finalCtx.textBaseline = 'middle';
    finalCtx.fillText(
      'Created with Kontentino • www.kontentino.com',
      finalCanvas.width / 2,
      canvas.height + watermarkHeight / 2
    );

    const dataUrl = finalCanvas.toDataURL('image/png');

    // Generate filename from post details
    const platform = post.platform;
    const date = post.date.replace(/-/g, '');
    const topic = post.topic.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30);
    const filename = `post-preview-${platform}-${topic}-${date}.png`;

    // Convert dataURL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    return {
      blob,
      dataUrl,
      filename
    };
  } catch (error) {
    // Ensure cleanup even if export fails
    if (document.body.contains(clonedElement)) {
      document.body.removeChild(clonedElement);
    }
    throw error;
  }
}
